import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Copy,
  Pin,
  Trash2,
  Users,
  LogOut,
} from 'lucide-react';
import { Button } from '@auw/workspace-ui';
import { Checkbox } from '@auw/workspace-ui';
import { DropdownMenu, type DropdownMenuItem } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { Modal } from '@auw/workspace-ui';
import { SelectDropdown, type SelectOption } from '@auw/workspace-ui';
import { ToggleSwitch } from '@auw/workspace-ui';
import { useChatStore } from '@auw/stores';
import { useTeamStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { useFeatureFlags } from '@auw/feature-flags';
import type {
  BrowserLiveViewPayload,
  ChatMessage,
  Conversation,
  ConversationSharedGroup,
  FileAttachment,
  SharedMemberProfile,
  WorkspaceFile,
  WorkspaceMode,
} from '@auw/workspace-utils';
import {
  createMessage,
  createShareLink,
  deleteSharedGroup,
  getShareLink,
  inviteSharedGroupMembers,
  leaveSharedGroup,
  listMessages,
  getWorkspaceLiveViewStatus,
  type ShareLinkResponse,
  updateItem,
  updateSharedGroupSettings,
} from '@auw/api-client';
import { buildConversationSettings as buildWorkspaceSettings } from '@auw/workspace-utils';
import {
  createGroupId,
  createSharePassword,
  extractHttpStatus,
  workspaceFileToAttachment,
} from '@auw/workspace-features';
import {
  WorkspaceHeaderCollabActions,
  WorkspaceHeaderFileActions,
  WorkspaceHeaderLiveViewAction,
  WorkspaceHeaderOverflowButton,
} from '@auw/workspace-features';
import {
  isConversationLiveViewAvailable,
  normalizeConversationLiveView,
} from '@auw/workspace-utils';

const MAX_GROUP_MEMBERS = 20;

interface Props {
  mode: WorkspaceMode;
  pageSidebarOpen: boolean;
}

/** 대화 단위 더보기 메뉴(핀 고정·삭제)를 두지 않는 모드.
 *  선적서류 에이전트는 왼쪽 현황판이 건 삭제를 맡는다. (GTC 비교 결과 화면은 이
 *  헤더 대신 자기 헤더를 슬롯으로 주입한다 — `getGtcAgentHeaderComponent`.) */
const MODES_WITHOUT_OVERFLOW = ['purchase-agent'] as const;

export const WorkspaceActionHeader: React.FC<Props> = ({ mode, pageSidebarOpen }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const activeConversation = useChatStore((s) => (s.activeId ? s.conversations[s.activeId] ?? null : null));
  const createConversation = useChatStore((s) => s.createConversation);
  const deleteConversation = useChatStore((s) => s.deleteConversation);
  const removeConversationLocal = useChatStore((s) => s.removeConversationLocal);
  const addToast = useChatStore((s) => s.addToast);
  const sessionFiles = useChatStore((s) => s.sessionFiles);
  const removeSessionFile = useChatStore((s) => s.removeSessionFile);
  const setPreviewFile = useChatStore((s) => s.setPreviewFile);
  const previewFile = useChatStore((s) => s.previewFile);
  const liveViewRuntime = useChatStore((s) => (s.activeId ? s.conversationRuntimes[s.activeId]?.liveView ?? null : null));
  const applyConversationLiveViewEvent = useChatStore((s) => s.applyConversationLiveViewEvent);
  const setConversationLiveViewOpen = useChatStore((s) => s.setConversationLiveViewOpen);
  const currentUser = useUserStore((s) => s.user);

  const teams = useTeamStore((s) => s.teams);
  const teamsLoaded = useTeamStore((s) => s.isLoaded);
  const loadMyTeams = useTeamStore((s) => s.loadMyTeams);
  const currentDetail = useTeamStore((s) => s.currentDetail);
  const loadTeamDetail = useTeamStore((s) => s.loadTeamDetail);

  /* P11-5: tenant policy toggles for header collab/live-view/share UI. */
  const featureFlags = useFeatureFlags();
  const groupChatEnabled = featureFlags.isEnabled('workspace.collab.groupChat');
  const liveViewEnabled = featureFlags.isEnabled('workspace.liveView');
  const sessionFilesEnabled = featureFlags.isEnabled('workspace.sessionFiles');
  const sharePublicViewEnabled = featureFlags.isEnabled('share.publicView');
  const sharePasswordProtectEnabled = featureFlags.isEnabled('share.passwordProtect');

  const [menu, setMenu] = useState<{ open: boolean; x: number; y: number }>({
    open: false,
    x: 0,
    y: 0,
  });
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const [eyeOpen, setEyeOpen] = useState(false);
  const eyeBtnRef = useRef<HTMLButtonElement>(null);
  const eyePanelRef = useRef<HTMLDivElement>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [checkedPersistedLiveViewKey, setCheckedPersistedLiveViewKey] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(`(max-width: ${theme.breakpoints.mobile})`);
    const syncViewport = () => setIsMobileViewport(media.matches);
    syncViewport();
    media.addEventListener('change', syncViewport);
    return () => media.removeEventListener('change', syncViewport);
  }, [theme.breakpoints.mobile]);

  const handleFileClick = useCallback((wf: WorkspaceFile) => {
    setPreviewFile(workspaceFileToAttachment(wf));
    setEyeOpen(false);
  }, [setPreviewFile]);

  const handleFileRemove = useCallback((e: React.MouseEvent, fileId: string) => {
    e.stopPropagation();
    void removeSessionFile(fileId);
  }, [removeSessionFile]);

  useEffect(() => {
    if (!eyeOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        eyePanelRef.current && !eyePanelRef.current.contains(e.target as Node) &&
        eyeBtnRef.current && !eyeBtnRef.current.contains(e.target as Node)
      ) {
        setEyeOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [eyeOpen]);

  useEffect(() => {
    setEyeOpen(false);
  }, [isMobileViewport]);

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [groupTitle, setGroupTitle] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [shareActionMode, setShareActionMode] = useState<'save' | 'clear' | null>(null);
  const creatingLink = shareActionMode !== null;
  const [loadingShareLink, setLoadingShareLink] = useState(false);
  const [hasExistingShareLink, setHasExistingShareLink] = useState(false);
  const [sharePasswordUnavailable, setSharePasswordUnavailable] = useState(false);
  const [shareResultText, setShareResultText] = useState('');
  const [copied, setCopied] = useState(false);
  const [sharedMemberAvatarErrorIds, setSharedMemberAvatarErrorIds] = useState<Record<string, boolean>>({});
  const isAgentLikeMode = mode === 'agent';
  /*
   * live-agent 는 첫 턴 완료(turn_complete) 시점에서야 `chatStore` 에
   * conversation 을 생성한다. 그래서 세션 준비 ~ 첫 유저 발화 ~ 첫 에이전트
   * 답변 직전까지는 `activeConversation === null` 이고, 기존 로직대로라면
   * 헤더가 렌더되지 않는다. 문제는 그 동안엔 유저 버블이 "원래 헤더가 들어갈
   * 공간" 까지 올라가다가, 첫 에이전트 답변이 오는 순간 conversation 이
   * 생성되며 헤더가 갑자기 등장해 메시지들이 한 번에 아래로 밀린다.
   *
   * live-agent 에서는 `activeConversation` 여부와 상관없이 헤더 영역을 항상
   * 선점하여 "첫 발화부터 헤더 밑" 위치가 유지되도록 한다. 내부 액션 아이콘/
   * 더보기 메뉴는 기존대로 `shouldShowActionIcons`(= activeConversation
   * 존재) 조건을 그대로 사용하므로, 초기 상태에서는 빈 헤더 공간만 자리를
   * 잡고 conversation 이 생긴 뒤에야 우측 컨트롤이 채워진다.
   */
  const isLiveAgentMode = mode === 'live-agent';

  const shouldShowHeader = useMemo(() => {
    if (isAgentLikeMode) return true;
    if (isLiveAgentMode) return true;
    if (!activeConversation) return false;
    if (activeConversation.mode !== mode || activeConversation.workspaceKind !== 'page') return false;
    return true;
  }, [activeConversation, isAgentLikeMode, isLiveAgentMode, mode]);

  // 대화 단위 더보기 메뉴(이름 변경·삭제)를 쓰지 않는 모드 — 그 화면이 자기 목록에서 관리한다 (구매 09-17).
  const hidesOverflow = MODES_WITHOUT_OVERFLOW.includes(
    (activeConversation?.mode ?? '') as (typeof MODES_WITHOUT_OVERFLOW)[number],
  );

  const shouldShowActionIcons = useMemo(() => {
    if (isAgentLikeMode) return true;
    if (!activeConversation) return false;
    return true;
  }, [activeConversation, isAgentLikeMode]);

  const markSharedMemberAvatarError = (avatarKey: string) => {
    setSharedMemberAvatarErrorIds((prev) => {
      if (prev[avatarKey]) return prev;
      return { ...prev, [avatarKey]: true };
    });
  };

  const sharedMembers =
    activeConversation?.workspaceScope === 'team'
      ? (activeConversation.sharedGroup?.members ?? [])
      : [];
  const isSharedGroupConversation = !!activeConversation?.sharedGroup;
  const hasShareableMessages = useMemo(() => {
    if (!activeConversation) return false;
    return activeConversation.messages.some((m) => m.role === 'user' || m.role === 'assistant');
  }, [activeConversation]);
  const effectiveLiveView = useMemo(() => {
    const persisted = activeConversation?.liveView ?? null;
    const hasRuntime = !!liveViewRuntime?.liveViewRef;
    const hasPersisted = !!persisted?.liveViewRef;
    if (!hasRuntime && !hasPersisted) return null;
    return {
      liveViewRef: liveViewRuntime?.liveViewRef ?? persisted?.liveViewRef ?? null,
      available: liveViewRuntime?.available ?? persisted?.available ?? false,
      watchOnly: liveViewRuntime?.watchOnly ?? persisted?.watchOnly ?? true,
      state: liveViewRuntime?.state ?? persisted?.state ?? null,
      errorMessage: liveViewRuntime?.errorMessage ?? persisted?.errorMessage ?? null,
      open: liveViewRuntime?.open ?? false,
      running: liveViewRuntime?.running ?? false,
    };
  }, [activeConversation?.liveView, liveViewRuntime]);
  const persistedOnlyLiveViewKey = useMemo(() => {
    const conversationId = activeConversation?.id;
    const persistedRef = activeConversation?.liveView?.liveViewRef;
    const runtimeRef = liveViewRuntime?.liveViewRef;
    if (!conversationId || !persistedRef || runtimeRef) return null;
    return `${conversationId}:${persistedRef}`;
  }, [activeConversation?.id, activeConversation?.liveView?.liveViewRef, liveViewRuntime?.liveViewRef]);
  const persistedLiveViewReadyForDisplay = !persistedOnlyLiveViewKey || checkedPersistedLiveViewKey === persistedOnlyLiveViewKey;
  const liveViewAvailable = useMemo(() => (
    persistedLiveViewReadyForDisplay && isConversationLiveViewAvailable(normalizeConversationLiveView({
      liveViewRef: effectiveLiveView?.liveViewRef ?? null,
      state: effectiveLiveView?.state ?? 'closed',
      available: effectiveLiveView?.available ?? false,
      watchOnly: effectiveLiveView?.watchOnly ?? true,
      errorMessage: effectiveLiveView?.errorMessage ?? null,
    }))
  ), [effectiveLiveView, persistedLiveViewReadyForDisplay]);
  const resolvedLiveViewWorkspaceType = activeConversation?.mode ?? mode;

  useEffect(() => {
    if (!persistedOnlyLiveViewKey) {
      setCheckedPersistedLiveViewKey(null);
      return;
    }
    setCheckedPersistedLiveViewKey((prev) => (prev === persistedOnlyLiveViewKey ? prev : null));
  }, [persistedOnlyLiveViewKey]);

  const applyClosedLiveView = useCallback((errorMessage?: string | null) => {
    if (!activeConversation?.id || !effectiveLiveView?.liveViewRef) return;
    const closedPayload: BrowserLiveViewPayload = {
      kind: 'browser_live_view',
      state: 'closed',
      live_view_ref: effectiveLiveView.liveViewRef,
      available: false,
      watch_only: effectiveLiveView.watchOnly,
      item_id: activeConversation.id,
      workspace_type: resolvedLiveViewWorkspaceType,
      updated_at: new Date().toISOString(),
      ...(errorMessage ? { error_message: errorMessage } : {}),
    };
    applyConversationLiveViewEvent(activeConversation.id, closedPayload, {
      autoOpen: false,
      running: false,
    });
  }, [
    activeConversation?.id,
    applyConversationLiveViewEvent,
    effectiveLiveView?.liveViewRef,
    effectiveLiveView?.watchOnly,
    mode,
    resolvedLiveViewWorkspaceType,
  ]);

  const handleToggleLiveView = useCallback(() => {
    if (!activeConversation?.id || !effectiveLiveView?.liveViewRef) return;
    setEyeOpen(false);
    setConversationLiveViewOpen(activeConversation.id, !(effectiveLiveView.open === true));
  }, [
    activeConversation?.id,
    effectiveLiveView?.liveViewRef,
    effectiveLiveView?.open,
    setConversationLiveViewOpen,
  ]);

  useEffect(() => {
    const persistedLiveView = activeConversation?.liveView;
    const persistedLiveViewRef = persistedLiveView?.liveViewRef;
    const activeConversationId = activeConversation?.id;
    if (!activeConversationId || !persistedLiveViewRef || !persistedLiveView?.available) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const { live_view } = await getWorkspaceLiveViewStatus(resolvedLiveViewWorkspaceType, {
          live_view_ref: persistedLiveViewRef,
          item_id: activeConversationId,
        });
        if (cancelled) return;
        if (persistedOnlyLiveViewKey) {
          setCheckedPersistedLiveViewKey(persistedOnlyLiveViewKey);
        }
        applyConversationLiveViewEvent(activeConversationId, live_view, {
          autoOpen: false,
          running: effectiveLiveView?.running ?? false,
        });
      } catch (err) {
        if (cancelled) return;
        if (persistedOnlyLiveViewKey) {
          setCheckedPersistedLiveViewKey(persistedOnlyLiveViewKey);
        }
        const status = extractHttpStatus(err);
        if (status === 404 || status === 409 || status === 410) {
          applyClosedLiveView(status === 409 ? 'live_view_unavailable' : null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeConversation?.id,
    activeConversation?.liveView,
    applyClosedLiveView,
    applyConversationLiveViewEvent,
    effectiveLiveView?.running,
    persistedOnlyLiveViewKey,
    resolvedLiveViewWorkspaceType,
  ]);

  const updateConversationLocal = (nextConv: Conversation, moveTop = false) => {
    useChatStore.setState((s) => {
      if (!s.conversations[nextConv.id]) return s;
      const conversationsNext = { ...s.conversations, [nextConv.id]: nextConv };
      if (!moveTop) return { conversations: conversationsNext };
      const moveArrayTop = (arr: string[]) => [nextConv.id, ...arr.filter((id) => id !== nextConv.id)];
      return {
        conversations: conversationsNext,
        conversationOrder: moveArrayTop(s.conversationOrder),
        workspaceOrder: moveArrayTop(s.workspaceOrder),
      };
    });
  };

  const handlePin = async () => {
    if (!activeConversation) return;
    const nowIso = new Date().toISOString();
    const nextConv: Conversation = {
      ...activeConversation,
      pinnedAt: nowIso,
    };
    updateConversationLocal(nextConv);

    try {
      if (nextConv.sharedGroup) {
        await updateSharedGroupSettings(nextConv.id, { pinned: true });
      } else {
        await updateItem(nextConv.id, { settings: buildWorkspaceSettings(nextConv) });
      }
      addToast({ type: 'success', title: t('toast.pinSuccess'), description: t('toast.pinSuccessDesc') });
    } catch (err) {
      updateConversationLocal(activeConversation);
      const status = extractHttpStatus(err);
      if (status === 409) {
        addToast({ type: 'warning', title: t('toast.pinFail'), description: t('error.teamSpaceDeleted') });
      } else {
        addToast({ type: 'error', title: t('toast.pinFail'), description: t('toast.retryLater') });
      }
    }
  };

  const handleUnpin = async () => {
    if (!activeConversation) return;
    const nextConv: Conversation = {
      ...activeConversation,
      pinnedAt: null,
    };
    updateConversationLocal(nextConv);

    try {
      if (nextConv.sharedGroup) {
        await updateSharedGroupSettings(nextConv.id, { pinned: false });
      } else {
        await updateItem(nextConv.id, { settings: buildWorkspaceSettings(nextConv) });
      }
      addToast({ type: 'success', title: t('toast.unpinSuccess'), description: t('toast.unpinSuccessDesc') });
    } catch (err) {
      updateConversationLocal(activeConversation);
      const status = extractHttpStatus(err);
      if (status === 409) {
        addToast({ type: 'warning', title: t('toast.unpinFail'), description: t('error.teamSpaceDeleted') });
      } else {
        addToast({ type: 'error', title: t('toast.unpinFail'), description: t('toast.retryLater') });
      }
    }
  };

  const handleLeaveGroup = async () => {
    if (!activeConversation?.sharedGroup) return;
    try {
      await leaveSharedGroup(activeConversation.id);
      removeConversationLocal(activeConversation.id);
      addToast({ type: 'success', title: t('workspace.leftChat'), description: t('workspace.leftChatDesc') });
    } catch (err) {
      const status = extractHttpStatus(err);
      if (status === 409) {
        addToast({ type: 'warning', title: t('workspace.leaveFail'), description: t('error.teamSpaceDeleted') });
      } else {
        addToast({ type: 'error', title: t('workspace.leaveFail'), description: t('toast.retryLater') });
      }
    }
  };

  const handleDelete = async () => {
    if (!activeConversation) return;
    if (!activeConversation.sharedGroup) {
      deleteConversation(activeConversation.id);
      return;
    }
    try {
      await deleteSharedGroup(activeConversation.id);
      removeConversationLocal(activeConversation.id);
      addToast({ type: 'success', title: t('workspace.groupChatDeleted') });
    } catch (err) {
      const status = extractHttpStatus(err);
      if (status === 403) {
        addToast({ type: 'warning', title: t('workspace.noDeletePermission'), description: t('workspace.noDeletePermissionDesc') });
      } else if (status === 409) {
        addToast({ type: 'warning', title: t('workspace.deleteFail'), description: t('error.teamSpaceDeleted') });
      } else {
        addToast({ type: 'error', title: t('workspace.deleteFail'), description: t('toast.retryLater') });
      }
    }
  };

  useEffect(() => {
    if (!groupModalOpen) return;
    if (!teamsLoaded) {
      void loadMyTeams();
    }
  }, [groupModalOpen, teamsLoaded, loadMyTeams]);

  useEffect(() => {
    if (!groupModalOpen) return;
    if (isSharedGroupConversation) {
      const fixedTeamId = activeConversation?.sharedGroup?.team_id || activeConversation?.workspaceTeamId || '';
      if (fixedTeamId) setSelectedTeamId(fixedTeamId);
      return;
    }
    if (teams.length === 0) return;
    setSelectedTeamId((prev) => prev || teams[0].team_id);
  }, [groupModalOpen, teams, isSharedGroupConversation, activeConversation]);

  useEffect(() => {
    if (!groupModalOpen || !selectedTeamId) return;
    void loadTeamDetail(selectedTeamId);
  }, [groupModalOpen, selectedTeamId, loadTeamDetail]);

  useEffect(() => {
    if (!groupModalOpen) return;
    if (activeConversation) {
      setGroupTitle(
        isSharedGroupConversation
          ? (activeConversation.sharedGroup?.title || activeConversation.title)
          : `${activeConversation.title} ${t('workspace.groupChatSuffix')}`,
      );
    } else {
      setGroupTitle(t('workspace.newGroupChat'));
    }
    setSelectedMemberIds(new Set());
    setMemberQuery('');
  }, [groupModalOpen, activeConversation, isSharedGroupConversation]);

  const teamMembers = useMemo(() => {
    if (!currentDetail) return [];
    if (currentDetail.team.team_id !== selectedTeamId) return [];
    return currentDetail.members;
  }, [currentDetail, selectedTeamId]);

  const currentUserId = currentUser?.userId ?? '';
  const existingGroupMemberIds = useMemo(
    () => new Set(isSharedGroupConversation ? (activeConversation?.sharedGroup?.member_ids ?? []) : []),
    [isSharedGroupConversation, activeConversation],
  );
  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return teamMembers
      .filter((m) => m.user_id !== currentUserId)
      .filter((m) => !existingGroupMemberIds.has(m.user_id))
      .filter((m) => {
        if (!q) return true;
        const name = (m.name ?? '').toLowerCase();
        const email = (m.email ?? '').toLowerCase();
        return name.includes(q) || email.includes(q);
      });
  }, [teamMembers, memberQuery, currentUserId, existingGroupMemberIds]);

  const toggleMember = (userId: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleCreateGroup = async () => {
    if (!selectedTeamId) {
      addToast({ type: 'warning', title: t('workspace.teamRequired'), description: t('workspace.teamRequiredDesc') });
      return;
    }
    if (selectedMemberIds.size === 0) {
      addToast({ type: 'warning', title: t('workspace.memberRequired'), description: t('workspace.memberRequiredDesc') });
      return;
    }

    setCreatingGroup(true);
    try {
      let baseConversation = activeConversation;
      if (!baseConversation) {
        const createdId = createConversation({ mode, scope: 'personal', kind: 'page' });
        baseConversation = useChatStore.getState().conversations[createdId] ?? null;
      }
      if (!baseConversation) {
        throw new Error('conversation_not_ready');
      }

      const teamName = teams.find((t) => t.team_id === selectedTeamId)?.name ?? null;
      const selectedProfiles: SharedMemberProfile[] = teamMembers
        .filter((m) => selectedMemberIds.has(m.user_id))
        .map((m) => ({
          user_id: m.user_id,
          name: m.name,
          email: m.email,
          picture: m.picture,
        }));

      const ownerProfile: SharedMemberProfile | null = currentUserId
        ? {
          user_id: currentUserId,
          name: currentUser?.name ?? null,
          email: currentUser?.email ?? null,
          picture: currentUser?.picture ?? null,
        }
        : null;

      const members = ownerProfile
        ? [ownerProfile, ...selectedProfiles.filter((m) => m.user_id !== ownerProfile.user_id)]
        : selectedProfiles;
      const memberIds = members.map((m) => m.user_id);
      const selfSenderName = (currentUser?.name || currentUser?.email || '').trim() || null;
      const normalizedBaseMessages: ChatMessage[] = baseConversation.messages.map((msg) => {
        if (msg.role !== 'user') return msg;
        if (msg.senderId || msg.senderName) return msg;
        return {
          ...msg,
          senderId: currentUserId || null,
          senderName: selfSenderName,
        };
      });

      const sharedGroup: ConversationSharedGroup = {
        group_id: createGroupId(),
        team_id: selectedTeamId,
        team_name: teamName,
        title: groupTitle.trim() || `${baseConversation.title} ${t('workspace.groupChatSuffix')}`,
        member_ids: memberIds,
        members,
        created_at: new Date().toISOString(),
      };

      const nextConv: Conversation = {
        ...baseConversation,
        workspaceScope: 'team',
        workspaceTeamId: selectedTeamId,
        sharedGroup,
        messages: normalizedBaseMessages,
        updatedAt: Date.now(),
      };
      updateConversationLocal(nextConv);
      try {
        await updateItem(nextConv.id, {
          scope: 'team',
          team_id: selectedTeamId,
          title: baseConversation.title,
          settings: buildWorkspaceSettings(nextConv),
        });
      } catch {
        updateConversationLocal(baseConversation);
        throw new Error('update_failed');
      }

      /* 기존 메시지 백필은 백엔드 히스토리가 비어 있을 때만 수행 (중복 저장 방지) */
      const existing = await listMessages(nextConv.id, { limit: 1 });
      if (existing.count === 0) {
        const existingMessages = normalizedBaseMessages.filter(
          (m) => (m.role === 'user' || m.role === 'assistant') && m.content,
        );
        for (const msg of existingMessages) {
          const metadata: Record<string, unknown> = {
            client_message_id: msg.id,
          };
          if (msg.senderId) metadata.senderId = msg.senderId;
          if (msg.senderName) metadata.senderName = msg.senderName;
          try {
            await createMessage(nextConv.id, {
              role: msg.role,
              content: msg.content,
              content_type: 'text',
              metadata,
            });
          } catch {
            /* 개별 메시지 실패는 무시 */
          }
        }
      }

      addToast({ type: 'success', title: t('workspace.collabCreated'), description: t('workspace.collabCreatedDesc') });
      setGroupModalOpen(false);
    } catch {
      addToast({ type: 'error', title: t('workspace.collabCreateFail'), description: t('toast.retryLater') });
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleInviteGroupMembers = async () => {
    if (!activeConversation?.sharedGroup) return;
    if (selectedMemberIds.size === 0) {
      addToast({ type: 'warning', title: t('workspace.memberRequired'), description: t('workspace.memberRequiredDesc') });
      return;
    }
    const existingCount = activeConversation.sharedGroup.member_ids?.length ?? 0;
    if (existingCount + selectedMemberIds.size > MAX_GROUP_MEMBERS) {
      addToast({ type: 'warning', title: t('workspace.memberOverflow'), description: t('workspace.memberOverflowDesc', { max: MAX_GROUP_MEMBERS, current: existingCount }) });
      return;
    }

    setCreatingGroup(true);
    try {
      await inviteSharedGroupMembers(activeConversation.id, {
        member_ids: Array.from(selectedMemberIds),
      });
      addToast({ type: 'success', title: t('workspace.memberInvited'), description: t('workspace.memberInvitedDesc') });
      setGroupModalOpen(false);
    } catch (err) {
      const status = extractHttpStatus(err);
      if (status === 409) {
        addToast({ type: 'warning', title: t('workspace.inviteFail'), description: t('workspace.inviteFailTeamDesc') });
      } else if (status === 400) {
        addToast({ type: 'warning', title: t('workspace.inviteFail'), description: t('workspace.inviteFailBadDesc') });
      } else {
        addToast({ type: 'error', title: t('workspace.inviteFail'), description: t('toast.retryLater') });
      }
    } finally {
      setCreatingGroup(false);
    }
  };

  const buildShareResultText = useCallback((shareToken: string, password?: string | null) => {
    const link = `${window.location.origin}/share/${shareToken}`;
    const trimmedPassword = password?.trim() ?? '';
    if (!trimmedPassword) return link;
    const userLabel = currentUser?.name || currentUser?.email || t('sidebar.user');
    return `${userLabel}님이 채팅을 공유했어요.\n\n${link}\n\n비밀번호: ${trimmedPassword}`;
  }, [currentUser?.email, currentUser?.name]);

  const applyShareLinkState = useCallback((share: ShareLinkResponse | null) => {
    setHasExistingShareLink(!!share);
    if (!share) {
      setPasswordEnabled(false);
      setSharePassword('');
      setShareResultText('');
      setSharePasswordUnavailable(false);
      return;
    }

    const nextPassword = share.password ?? '';
    setPasswordEnabled(!share.is_public);
    setSharePassword(nextPassword);
    setShareResultText(buildShareResultText(share.share_token, nextPassword));
    setSharePasswordUnavailable(!share.is_public && share.has_password && nextPassword.length === 0);
  }, [buildShareResultText]);

  const ensureShareMessagesSynced = useCallback(async () => {
    if (!activeConversation) return;
    const existing = await listMessages(activeConversation.id, { limit: 1 });
    if (existing.count > 0) return;

    const syncable = activeConversation.messages.filter(
      (m: ChatMessage) =>
        !m.isStreaming
        && (m.role === 'user' || m.role === 'assistant')
        && m.content.trim().length > 0,
    );
    for (const message of syncable) {
      // 공유 시 백엔드 기준 메시지가 비어있으면 현재 페이지 대화를 먼저 동기화합니다.
      // eslint-disable-next-line no-await-in-loop
      await createMessage(activeConversation.id, {
        role: message.role,
        content: message.content,
        content_type: 'text',
      });
    }
  }, [activeConversation]);

  useEffect(() => {
    if (!shareModalOpen) return;

    const convId = activeConversation?.id ?? '';
    let cancelled = false;

    setCopied(false);
    setLoadingShareLink(true);
    applyShareLinkState(null);

    if (!convId) {
      setLoadingShareLink(false);
      return;
    }

    void (async () => {
      try {
        const share = await getShareLink(convId);
        if (cancelled) return;
        applyShareLinkState(share);
      } catch (err) {
        if (cancelled) return;
        const status = extractHttpStatus(err);
        if (status !== 404) {
          addToast({ type: 'error', title: t('workspace.shareLoadFail'), description: t('toast.retryLater') });
        }
        applyShareLinkState(null);
      } finally {
        if (!cancelled) {
          setLoadingShareLink(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shareModalOpen, activeConversation?.id, addToast, applyShareLinkState]);

  const handleCreateShareLink = async () => {
    if (!activeConversation) return;
    let password = sharePassword.trim();
    if (passwordEnabled && !password) {
      password = createSharePassword();
      setSharePassword(password);
    }

    setShareActionMode('save');
    try {
      await ensureShareMessagesSynced();
      const result = await createShareLink(activeConversation.id, {
        public: !passwordEnabled,
        ...(passwordEnabled ? { password } : {}),
      });
      applyShareLinkState({
        ...result,
        password: result.password ?? (result.is_public ? null : password),
        has_password: result.is_public ? false : (result.has_password || password.length > 0),
      });
      setCopied(false);
    } catch {
      addToast({ type: 'error', title: t('workspace.shareSaveFail'), description: t('toast.retryLater') });
    } finally {
      setShareActionMode(null);
    }
  };

  const handleDisableSharePassword = async () => {
    if (!activeConversation) return;

    setShareActionMode('clear');
    try {
      await ensureShareMessagesSynced();
      const result = await createShareLink(activeConversation.id, { public: true });
      applyShareLinkState(result);
      setCopied(false);
      addToast({ type: 'success', title: t('workspace.passwordCleared'), description: t('workspace.passwordClearedDesc') });
    } catch {
      addToast({ type: 'error', title: t('workspace.passwordClearFail'), description: t('toast.retryLater') });
    } finally {
      setShareActionMode(null);
    }
  };

  const handleCopyShareText = async () => {
    if (!shareResultText) return;
    try {
      await navigator.clipboard.writeText(shareResultText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      addToast({ type: 'error', title: t('workspace.copyFail'), description: t('workspace.copyFailDesc') });
    }
  };

  const menuItems: DropdownMenuItem[] = activeConversation ? [
    activeConversation?.pinnedAt
      ? {
          id: 'unpin',
          label: t('tooltip.unpin'),
          icon: <Pin size={14} />,
          onClick: handleUnpin,
        }
      : {
          id: 'pin',
          label: t('tooltip.pin'),
          icon: <Pin size={14} />,
          onClick: handlePin,
        },
    ...(isSharedGroupConversation
      ? [
          {
            id: 'leave',
            label: t('workspace.leaveChat'),
            icon: <LogOut size={14} />,
            onClick: handleLeaveGroup,
          },
        ]
      : []),
    {
      id: 'delete',
      label: isSharedGroupConversation ? t('workspace.deleteSharedGroupItem') : t('workspace.deleteItem'),
      icon: <Trash2 size={14} />,
      danger: true,
      onClick: () => void handleDelete(),
    },
  ] : [];

  /* P11-5: gate live-view trigger and session-files panel via tenant flags. */
  const shouldShowLiveViewAction = liveViewEnabled && liveViewAvailable;
  const shouldShowFileAction = sessionFilesEnabled && sessionFiles.length > 0;
  const shouldRenderRightActions = shouldShowActionIcons || (isMobileViewport && (shouldShowFileAction || shouldShowLiveViewAction));
  const shouldShowGroupAction = shouldShowActionIcons
    && mode !== 'live-agent';
  const leftHeaderAction = (shouldShowFileAction || shouldShowLiveViewAction) ? (
    <LeftActions>
      {shouldShowFileAction && (
        <WorkspaceHeaderFileActions
          sessionFiles={sessionFiles}
          previewFileId={previewFile?.id ?? null}
          open={eyeOpen}
          buttonRef={eyeBtnRef}
          panelRef={eyePanelRef}
          onToggle={() => setEyeOpen((value) => !value)}
          onFileClick={handleFileClick}
          onFileRemove={handleFileRemove}
        />
      )}
      {shouldShowLiveViewAction && (
        <WorkspaceHeaderLiveViewAction
          available={liveViewAvailable}
          open={effectiveLiveView?.open === true}
          running={effectiveLiveView?.running === true}
          onToggle={handleToggleLiveView}
        />
      )}
    </LeftActions>
  ) : null;

  if (!shouldShowHeader) return null;

  return (
    <>
      <Header $sidebarOpen={pageSidebarOpen}>
        {!isMobileViewport && leftHeaderAction}
        {shouldRenderRightActions && (
          <RightActions>
            {shouldShowActionIcons && (
              <WorkspaceHeaderCollabActions
                title={activeConversation?.sharedGroup?.title ?? t('workspace.groupChat')}
                conversationId={activeConversation?.id ?? null}
                members={sharedMembers}
                currentUserId={currentUser?.userId ?? null}
                currentUserPicture={currentUser?.picture ?? null}
                avatarErrorIds={sharedMemberAvatarErrorIds}
                onAvatarError={markSharedMemberAvatarError}
                /* P11-5: hide share affordance when tenant disables share.publicView. */
                hasShareableMessages={sharePublicViewEnabled && hasShareableMessages}
                onOpenShare={() => setShareModalOpen(true)}
                /* P11-5: hide collab/group affordance when workspace.collab.groupChat is off. */
                shouldShowGroupAction={groupChatEnabled && shouldShowGroupAction}
                isSharedGroupConversation={isSharedGroupConversation}
                onOpenGroup={() => setGroupModalOpen(true)}
              />
            )}
            {isMobileViewport && leftHeaderAction}
            {shouldShowActionIcons && (
              <>
                {activeConversation && !hidesOverflow && (
                  <WorkspaceHeaderOverflowButton
                    buttonRef={moreBtnRef}
                    onOpen={() => {
                      const rect = moreBtnRef.current?.getBoundingClientRect();
                      if (!rect) return;
                      setMenu({
                        open: true,
                        x: rect.right,
                        y: rect.bottom + 4,
                      });
                    }}
                  />
                )}
              </>
            )}
          </RightActions>
        )}
      </Header>

      <DropdownMenu
        open={menu.open}
        x={menu.x}
        y={menu.y}
        direction="down"
        align="right"
        onClose={() => setMenu((prev) => ({ ...prev, open: false }))}
        items={menuItems}
      />

      <Modal
        open={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        title={isSharedGroupConversation ? t('workspace.inviteMembers') : t('workspace.startGroupChat')}
        width="560px"
      >
        <ModalSection>
          <FieldLabel>{t('sidebar.teamWorkspace')}</FieldLabel>
          {isSharedGroupConversation ? (
            <ReadonlyFieldValue>
              {teams.find((team) => team.team_id === selectedTeamId)?.name
                ?? currentDetail?.team?.name
                ?? activeConversation?.sharedGroup?.team_name
                ?? t('sidebar.team')}
            </ReadonlyFieldValue>
          ) : (
            <SelectDropdown
              options={teams.map((team) => ({
                value: team.team_id,
                label: team.name,
                icon: <Users size={14} />,
              }))}
              value={selectedTeamId}
              onChange={setSelectedTeamId}
              placeholder={t('workspace.selectTeamPlaceholder')}
            />
          )}
        </ModalSection>

        {!isSharedGroupConversation && (
          <ModalSection>
            <FieldLabel>{t('workspace.groupTitle')}</FieldLabel>
            <Input
              value={groupTitle}
              onChange={(e) => setGroupTitle(e.target.value)}
              placeholder={t('workspace.groupTitlePlaceholder')}
            />
          </ModalSection>
        )}

        <ModalSection>
          <FieldLabel>{isSharedGroupConversation ? t('workspace.searchMemberForInvite') : t('workspace.searchMember')}</FieldLabel>
          <Input
            value={memberQuery}
            onChange={(e) => setMemberQuery(e.target.value)}
            placeholder={t('workspace.searchMemberPlaceholder')}
          />
        </ModalSection>

        <MemberList>
          {filteredMembers.map((member) => {
            const checked = selectedMemberIds.has(member.user_id);
            const memberPicture = member.user_id === currentUser?.userId
              ? (currentUser?.picture ?? null)
              : (member.picture ?? null);
            const avatarKey = `member-search:${member.user_id}:${memberPicture || 'none'}`;
            const fallback = (member.name || member.email || member.user_id).charAt(0).toUpperCase();
            return (
              <MemberItem key={member.user_id} onClick={() => toggleMember(member.user_id)}>
                <MemberLeft>
                  <span onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleMember(member.user_id)}
                      size="sm"
                    />
                  </span>
                  <MemberAvatar>
                    {memberPicture && !sharedMemberAvatarErrorIds[avatarKey] ? (
                      <AvatarImg
                        src={memberPicture}
                        alt={member.name || member.user_id}
                        onError={() => markSharedMemberAvatarError(avatarKey)}
                      />
                    ) : (
                      <span>{fallback}</span>
                    )}
                  </MemberAvatar>
                  <MemberText>
                    <div>{member.name || member.user_id}</div>
                    <small>{member.email || ''}</small>
                  </MemberText>
                </MemberLeft>
              </MemberItem>
            );
          })}
          {filteredMembers.length === 0 && (
            <EmptyState
              description={isSharedGroupConversation
                ? t('workspace.noInvitableMembers')
                : t('workspace.noSelectableMembers')}
            />
          )}
        </MemberList>

        <FooterRow>
          <Button
            variant="secondary"
            onClick={() => setGroupModalOpen(false)}
          >
            {t('button.cancel')}
          </Button>
          <Button
            variant="primary"
            isLoading={creatingGroup}
            onClick={isSharedGroupConversation ? handleInviteGroupMembers : handleCreateGroup}
          >
            {isSharedGroupConversation ? t('workspace.invite') : t('button.confirm')}
          </Button>
        </FooterRow>
      </Modal>

      <Modal
        open={shareModalOpen && sharePublicViewEnabled}
        onClose={() => setShareModalOpen(false)}
        title={t('workspace.shareLink')}
        width="560px"
      >
        {sharePasswordProtectEnabled && (
          <ModalSection>
            <ToggleRow>
              <div>
                <FieldLabel>{t('workspace.createPassword')}</FieldLabel>
                <HelpText>
                  {loadingShareLink
                    ? t('workspace.shareHelpLoading')
                    : t('workspace.shareHelpPublic')}
                </HelpText>
              </div>
              <ToggleSwitch
                checked={passwordEnabled}
                onChange={setPasswordEnabled}
              />
            </ToggleRow>
          </ModalSection>
        )}

        {passwordEnabled && (
          <ModalSection>
            <FieldLabel>{t('workspace.password')}</FieldLabel>
            <PasswordRow>
              <Input
                value={sharePassword}
                onChange={(e) => setSharePassword(e.target.value)}
                placeholder={t('workspace.passwordPlaceholder')}
              />
              <Button
                variant="secondary"
                onClick={() => setSharePassword(createSharePassword())}
              >
                {t('workspace.generate')}
              </Button>
            </PasswordRow>
            {sharePasswordUnavailable && (
              <HelpText>{t('workspace.passwordNotDisplayable')}</HelpText>
            )}
          </ModalSection>
        )}

        <FooterRow>
          <Button
            variant="secondary"
            onClick={() => setShareModalOpen(false)}
          >
            {t('button.close')}
          </Button>
          {hasExistingShareLink && passwordEnabled && (
            <Button
              variant="secondary"
              isLoading={shareActionMode === 'clear'}
              disabled={loadingShareLink || (creatingLink && shareActionMode !== 'clear')}
              onClick={handleDisableSharePassword}
            >
              {t('workspace.disablePassword')}
            </Button>
          )}
          <Button
            variant="primary"
            isLoading={shareActionMode === 'save'}
            disabled={loadingShareLink || (creatingLink && shareActionMode !== 'save')}
            onClick={handleCreateShareLink}
          >
            {hasExistingShareLink ? t('workspace.saveSettings') : t('workspace.generateLink')}
          </Button>
        </FooterRow>

        {shareResultText && (
          <ResultCard>
            <ResultPre>{shareResultText}</ResultPre>
            <Button
              variant="secondary"
              leftIcon={copied ? <Check size={14} /> : <Copy size={14} />}
              onClick={handleCopyShareText}
            >
              {copied ? t('workspace.copied') : t('workspace.copyLink')}
            </Button>
          </ResultCard>
        )}
      </Modal>

    </>
  );
};

const Header = styled.div<{ $sidebarOpen: boolean }>`
  position: sticky;
  top: 0;
  z-index: 6;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  padding-left: ${({ $sidebarOpen }) => ($sidebarOpen ? '16px' : '48px')};
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  transition: padding-left ${({ theme }) => theme.transitions.fast};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    position: fixed;
    top: 0;
    right: 0;
    left: auto;
    min-height: 48px;
    width: auto;
    padding: 8px 16px;
    background: transparent;
    z-index: 7;
    pointer-events: none;

    > * {
      pointer-events: auto;
    }
  }
`;

const LeftActions = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
`;

const FieldLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ReadonlyFieldValue = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  line-height: 1.4;
  padding: 10px 12px;
`;

const HelpText = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;


const MemberList = styled.div`
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 6px;
  margin-bottom: 14px;
`;

const MemberItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const MemberLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MemberAvatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 22%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.mode === 'dark' ? 'linear-gradient(145deg, #434345, #38383a)' : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) => theme.mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  font-size: 0.72rem;
  font-weight: 700;
`;

const MemberText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.82rem;

  small {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 0.72rem;
  }
`;


const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const PasswordRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResultCard = styled.div`
  margin-top: 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ResultPre = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ${({ theme }) => theme.fonts.sans};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.82rem;
  line-height: 1.45;
`;
